import {Injectable, NgZone} from '@angular/core';
import {defer, from, Observable, of, throwError} from 'rxjs';
import {catchError, delay, switchMap} from 'rxjs/operators';
import {LoggerService} from '../logger.service';
import {CaptchaService} from "./captcha.service";
import {AwsWafTokenService} from './aws-waf-token.service';

declare const AwsWafIntegration: any;

@Injectable({
  providedIn: 'root'
})
export class AwsWafWrapperService {

  private static readonly retryInterval = 100;
  private static readonly maxRetries = 50;

  constructor(private captchaService: CaptchaService,
              private awsWafTokenService: AwsWafTokenService,
              private loggerService: LoggerService,
              private ngZone: NgZone) {
  }

  public get(url: string): Observable<any> {
    return this.fetchWithRetry(url, {method: 'GET', headers: {}});
  }

  public post(url: string, body: any, headers: any): Observable<any> {
    return this.fetchWithRetry(url, {method: 'POST', headers: headers, body: body});
  }

  private fetchWithRetry(
    url: string,
    options: any,
    wafTokenSource: Observable<string | null> = defer(() => this.awsWafTokenService.getToken(false)),
    challengeRetriesLeft: number = AwsWafWrapperService.maxRetries
  ): Observable<any> {
    return wafTokenSource.pipe(
      switchMap((token: string | null) => {
        options.headers = options.headers || {};
        if (token) {
          options.headers['x-aws-waf-token'] = token;
        } else {
          delete options.headers['x-aws-waf-token'];
        }
        return this.executeFetchInNgZone(url, options);
      }),
      switchMap((response: Response) => this.handleFetchResponse(response, url, options, challengeRetriesLeft)),
      catchError((error: any) => this.handleFetchError(error, url))
    );
  }

  private executeFetchInNgZone(url: string, options: any): Observable<Response> {
    return new Observable<Response>(observer => {
      try {
        this.resolveFetchFunction(url)(url, options).then((response: Response) => {
          this.ngZone.run(() => {
            observer.next(response);
            observer.complete();
          });
        }).catch((error: any) => {
          this.ngZone.run(() => {
            observer.error(error);
          });
        });
      } catch (error: any) {
        this.ngZone.run(() => {
          observer.error(error);
        });
      }
    });
  }

  private resolveFetchFunction(url: string): (fetchUrl: string, fetchOptions: any) => Promise<Response> {
    if (typeof (window as any).AwsWafIntegration?.fetch === 'function') {
      return (fetchUrl: string, fetchOptions: any) => AwsWafIntegration.fetch(fetchUrl, fetchOptions);
    }
    if (this.awsWafTokenService.isIntegrationAvailable()) {
      this.loggerService.ddError(`AwsWafIntegration is not available, ${url} API call is made with the native fetch and without the x-aws-waf-token header`);
    }
    return (fetchUrl: string, fetchOptions: any) => window.fetch(fetchUrl, fetchOptions);
  }

  private handleFetchResponse(
    response: Response,
    url: string,
    options: any,
    challengeRetriesLeft: number
  ): Observable<any> {
    switch (response.status) {
      case 200:
        this.loggerService.info(`${url} API call completed successfully`);
        return from(response.json());
      case 202:
        return this.handleChallengeResponse(url, options, challengeRetriesLeft);
      case 405:
        return this.handleCaptchaResponse(url, options);
      default:
        this.loggerService.ddError(`${url} API call failed with status code ${response.status}`);
        return throwError(() => new Error(`Http response is unsuccessful: ${response.status}`));
    }
  }

  private handleChallengeResponse(
    url: string,
    options: any,
    challengeRetriesLeft: number
  ): Observable<any> {
    if (challengeRetriesLeft > 0) {
      this.loggerService.ddInfo(`${url} API call failed with Challenge, retrying... Retries left: ${challengeRetriesLeft - 1}`);
      return this.awaitChallengeRetryDelay().pipe(
        switchMap(() => this.fetchWithRetry(
          url,
          options,
          defer(() => this.awsWafTokenService.getToken(true)),
          challengeRetriesLeft - 1
        ))
      );
    }
    return throwError(() => new Error('Max retries reached without resolving the challenge.'));
  }

  private awaitChallengeRetryDelay(): Observable<any> {
    if (this.awsWafTokenService.isIntegrationAvailable()) {
      return of(null);
    }
    return of(null).pipe(delay(AwsWafWrapperService.retryInterval));
  }

  private handleCaptchaResponse(
    url: string,
    options: any
  ): Observable<any> {
    this.loggerService.ddInfo(`${url} API call failed with Captcha`);
    return this.captchaService.handleCaptchaResponse()
      .pipe(switchMap(token => this.fetchWithRetry(url, options, of(token))));
  }

  private handleFetchError(error: any, url: string): Observable<never> {
    this.loggerService.ddError(`Error during ${url} API call: ${error}`, error);
    return throwError(() => new Error(`API call error: ${error?.message || error}`));
  }
}
